import React from 'react';
import { AdvancedMarker, AdvancedMarkerProps } from '@vis.gl/react-google-maps';
import { OrderStatus } from '@src/pages/Deliveries/context/orders';
import PIEText from '@src/shared/components/PIEText';
import { Statuses } from '@src/pages/DeliveryDashboard/models/model-constants';
import { IconClockAlert } from '@justeattakeaway/pie-icons-react';
import styles from './OrderMapMarker.module.scss';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import PIETag, { ETagsColorType } from '@src/shared/components/PIETag';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import MapCard from '../../MapCard';
import { globalStyles } from 'snacks-design-system';
import MapPin from '../../MapPin';
import OrderMapMarkerIcon from './OrderMapMarkerIcon';
import { mapPinColorGenerator } from '../../MapPin/helpers';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
interface OrderMapMarkerProps {
  orderStatus: OrderStatus;
  customerInformation?: any;
  showCard?: Boolean;
  dueTime: string | boolean;
  updateDate?: string;
  onClick?: () => void;
  isLateOrder?: boolean;
}

const { xdsColorContentLight, xdsColorSupportPositive } = globalStyles;

export const OrderMapMarker = ({
  orderStatus,
  position,
  onClick,
  showCard,
  dueTime,
  customerInformation,
  isLateOrder,
  updateDate,
}: AdvancedMarkerProps & OrderMapMarkerProps) => {
  const { isResolutionMobile } = useDeviceDetect();
  const { t } = useTranslation();

  const customerInformationLimiter = (text: string) => {
    if (isResolutionMobile) {
      return text.length > 11 ? `${text.slice(0, 8)}...` : text;
    }
    return text.length > 19 ? `${text.slice(0, 16)}...` : text;
  };

  const renderDueTime = () => {
    if (isLateOrder) {
      return (
        <PIEComponentUtil marginLeft="var(--xds-spacing-b)">
          <PIETag
            className={styles.OrderMapMarkerLateTag}
            icon={isResolutionMobile ? <></> : <IconClockAlert size="xs" />}
            mode={ETagsColorType.Error}
            size={isResolutionMobile ? 'small' : 'large'}
          >
            {dueTime}
          </PIETag>
        </PIEComponentUtil>
      );
    } else {
      return (
        <PIEText
          size="var(--xds-font-size-14)"
          fontWeight="var(--xds-font-weight-bold)"
          text={dueTime}
          customStyle={{ marginLeft: 'var(--xds-spacing-b)' }}
        />
      );
    }
  };
  return (
    <>
      {orderStatus !== Statuses.Delivered ? (
        <AdvancedMarker
          position={position}
          onClick={() => {
            onClick && onClick();
          }}
        >
          <div className={styles.OrderMapMarker}>
            <OrderMapMarkerIcon orderStatus={orderStatus} />

            <div style={{ width: 'max-content', marginLeft: 'var(--xds-spacing-b)' }}>
              <PIEText
                size="var(--xds-font-size-14)"
                fontWeight={`var(--xds-font-weight-${
                  orderStatus === Statuses.Unassigned ? 'bold' : 'regular'
                })`}
                text={customerInformationLimiter(customerInformation)}
              />
            </div>
            {renderDueTime()}
          </div>
        </AdvancedMarker>
      ) : (
        <>
          {showCard ? (
            <AdvancedMarker
              position={position}
              onClick={() => {
                onClick && onClick();
              }}
            >
              <MapCard backgroundColor={xdsColorContentLight}>
                <PIEComponentUtil marginTop={'var(--xds-spacing-a)'}>
                  <PIEText
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-regular)"
                    text={customerInformationLimiter(customerInformation)}
                    align="center"
                  />
                </PIEComponentUtil>

                <PIETag mode={ETagsColorType.Success} className={styles.OrderMapMarkerDeliveredTag}>
                  <PIEText
                    size="var(--xds-font-size-14)"
                    fontWeight="var(--xds-font-weight-bold)"
                    text={`${t('order.deliveredAt')} ${dayjs(updateDate).format('HH:mm')}`}
                    align="center"
                  />
                </PIETag>
              </MapCard>
            </AdvancedMarker>
          ) : null}

          <AdvancedMarker
            clickable={true}
            position={position}
            onClick={() => {
              onClick && onClick();
            }}
          >
            <MapPin {...mapPinColorGenerator('#fff', xdsColorSupportPositive)}>
              <OrderMapMarkerIcon orderStatus={orderStatus} />
            </MapPin>
          </AdvancedMarker>
        </>
      )}
    </>
  );
};

export default OrderMapMarker;
